import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { Chain, ChainPath } from '@ankr.com/chains-list';
import { useCallback } from 'react';
import { CheckboxClasses } from '@mui/material/Checkbox/checkboxClasses';

import { EndpointGroup } from 'modules/endpoints/types';
import { getDisabledChainTooltipText } from 'modules/chains/utils/getDisabledChainTooltipText';

import { getCustomLabelForChainsCornerCases } from '../../utils/getCustomLabelForChainsCornerCases';
import { getChainName } from '../../utils/getChainName';
import { getPathFromSubchain } from './useChainTypeSelectorWithSubchains';

interface ISubchainSelectorProps {
  isLoading?: boolean;
  classes?: Partial<CheckboxClasses>;
  checkBoxClassName?: string;
  subChain: Chain;
  endpoints: EndpointGroup[];
  selectedChainPaths: ChainPath[];
  shouldAvoidChainNameFormatting?: boolean;
  handleChangeChild: (path: ChainPath) => void;
  isDisabled?: boolean;
}

export const SubchainSelector = ({
  checkBoxClassName,
  classes,
  endpoints,
  handleChangeChild,
  isDisabled,
  isLoading,
  selectedChainPaths,
  shouldAvoidChainNameFormatting,
  subChain,
}: ISubchainSelectorProps) => {
  const currentEndpoint = endpoints.find(endpoint =>
    endpoint.chains.some(endpointChain => endpointChain.id === subChain.id),
  );

  const path = getPathFromSubchain(subChain);

  const label = currentEndpoint
    ? getCustomLabelForChainsCornerCases({
        chainId: subChain.id,
        label: currentEndpoint.name,
      })
    : getChainName(subChain, shouldAvoidChainNameFormatting);

  const onChangeChild = useCallback(
    () => handleChangeChild(path),
    [handleChangeChild, path],
  );

  const isChecked = selectedChainPaths.includes(path);
  const disabled = isChecked && isDisabled;

  return (
    <Tooltip
      placement="left"
      title={getDisabledChainTooltipText(Boolean(disabled))}
    >
      <FormControlLabel
        classes={classes}
        control={
          <Checkbox
            disabled={isLoading || disabled}
            className={checkBoxClassName}
            checked={isChecked}
            onClick={onChangeChild}
          />
        }
        label={label}
      />
    </Tooltip>
  );
};
